import { useState, useEffect, useRef, useMemo } from 'react';
import { useTenant } from '../context/TenantContext.js';
import { tenants as tenantsApi, plans as plansApi, uploadFile } from '../api/client.js';
import { useApi } from '../hooks/useApi.js';
import { LoadingSpinner } from '../components/LoadingSpinner.js';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { useToast } from '../components/Toast.js';
import type { Plan } from '../types/index.js';

const DEFAULT_EMBED = {
  title: 'Select Your Plan',
  description: 'Choose a subscription plan below to get started.',
  color: 0x5865f2,
  footerText: 'Payments processed via crypto',
  footerIconUrl: '',
  authorName: '',
  authorIconUrl: '',
  authorUrl: '',
  thumbnailUrl: '',
  imageUrl: '',
};

const COLOR_PRESETS = [
  { hex: '#5865f2', value: 0x5865f2 },
  { hex: '#2ecc71', value: 0x2ecc71 },
  { hex: '#e74c3c', value: 0xe74c3c },
  { hex: '#f39c12', value: 0xf39c12 },
  { hex: '#9b59b6', value: 0x9b59b6 },
  { hex: '#1abc9c', value: 0x1abc9c },
  { hex: '#e91e63', value: 0xe91e63 },
  { hex: '#ffffff', value: 0xffffff },
];

/* inline editable text — renders as normal text, becomes an input on click */
function InlineText({ value, onChange, placeholder, style, multiline }: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  style?: React.CSSProperties;
  multiline?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const ref = useRef<HTMLTextAreaElement | HTMLInputElement>(null);

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      ref.current.select();
    }
  }, [editing]);

  const base: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    outline: 'none',
    padding: 0,
    margin: 0,
    width: '100%',
    fontFamily: 'inherit',
    resize: 'none',
    ...style,
  };

  if (editing) {
    if (multiline) {
      return (
        <textarea
          ref={ref as React.RefObject<HTMLTextAreaElement>}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
          placeholder={placeholder}
          rows={3}
          style={{ ...base, color: style?.color ?? '#b5bac1' }}
        />
      );
    }
    return (
      <input
        ref={ref as React.RefObject<HTMLInputElement>}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditing(false); }}
        placeholder={placeholder}
        style={{ ...base, color: style?.color ?? '#f2f3f5' }}
      />
    );
  }

  return (
    <div
      onClick={() => setEditing(true)}
      style={{
        ...style,
        cursor: 'text',
        borderRadius: 3,
        padding: '1px 3px',
        margin: '-1px -3px',
        minHeight: multiline ? 40 : undefined,
        whiteSpace: multiline ? 'pre-wrap' : undefined,
        transition: 'background 0.1s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {value || <span style={{ opacity: 0.4 }}>{placeholder}</span>}
    </div>
  );
}

/* clickable image area — click opens file picker, uploads, and sets URL */
function InlineImage({ url, onChangeUrl, label, imgStyle, placeholderStyle, onError }: {
  url: string;
  onChangeUrl: (v: string) => void;
  label: string;
  imgStyle: React.CSSProperties;
  placeholderStyle?: React.CSSProperties;
  onError?: (msg: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const uploadedUrl = await uploadFile(file);
      onChangeUrl(uploadedUrl);
    } catch (err) {
      console.error('[embed] upload failed:', err);
      const msg = err instanceof Error ? err.message : 'Upload failed';
      onError ? onError(msg) : console.error(msg);
    } finally {
      setUploading(false);
      // reset so same file can be re-selected
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const openPicker = () => fileRef.current?.click();

  // hidden file input shared by all states
  const fileInput = (
    <input
      ref={fileRef}
      type="file"
      accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
      onChange={(e) => void handleFileSelect(e)}
      style={{ display: 'none' }}
    />
  );

  if (uploading) {
    return (
      <div style={{
        ...placeholderStyle,
        border: '2px dashed #5865f2',
        borderRadius: imgStyle.borderRadius ?? 4,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#b5bac1', fontSize: 11,
      }}>
        {fileInput}
        Uploading...
      </div>
    );
  }

  if (url) {
    return (
      <div
        style={{ position: 'relative', cursor: 'pointer', ...placeholderStyle }}
        onClick={openPicker}
        title={`Click to replace ${label.toLowerCase()}`}
      >
        {fileInput}
        <img src={url} alt="" style={imgStyle} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,.45)', borderRadius: imgStyle.borderRadius ?? 4,
          display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s',
        }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0'; }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5Z" /></svg>
        </div>
      </div>
    );
  }

  return null;
}

function ImagePlaceholder({ label, style, onPickFile }: {
  label: string;
  style?: React.CSSProperties;
  onPickFile: (file: File) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      onPickFile(file);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div
      onClick={() => fileRef.current?.click()}
      style={{
        border: '2px dashed #4e5058',
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        cursor: 'pointer',
        color: '#80848e',
        fontSize: 11,
        fontWeight: 500,
        transition: 'border-color 0.15s, color 0.15s',
        ...style,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#5865f2'; e.currentTarget.style.color = '#b5bac1'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#4e5058'; e.currentTarget.style.color = '#80848e'; }}
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
        onChange={(e) => void handleFile(e)}
        style={{ display: 'none' }}
      />
      {uploading ? 'Uploading...' : (
        <>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="m21 15-5-5L5 21" />
          </svg>
          {label}
        </>
      )}
    </div>
  );
}

/* small icon button used for actions alongside embed sections */
function ActionBtn({ icon, title, onClick, color }: { icon: React.ReactNode; title: string; onClick: () => void; color?: string }) {
  return (
    <div
      title={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={{
        width: 22, height: 22, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', color: color ?? '#80848e', transition: 'background 0.15s, color 0.15s', flexShrink: 0,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#f2f3f5'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = color ?? '#80848e'; }}
    >
      {icon}
    </div>
  );
}

const TrashIcon = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>;
const PlusIcon = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>;

export function EmbedsPage() {
  const { activeTenantId, activeTenant, refreshActiveTenant, loading: tenantLoading, error: tenantError, refreshTenants } = useTenant();
  const { toast } = useToast();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(0x5865f2);
  const [customHex, setCustomHex] = useState('');
  const [footerText, setFooterText] = useState('');
  const [footerIconUrl, setFooterIconUrl] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [authorIconUrl, setAuthorIconUrl] = useState('');
  const [authorUrl, setAuthorUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const colorPickerRef = useRef<HTMLInputElement>(null);
  const colorPopupRef = useRef<HTMLDivElement>(null);
  const addMenuRef = useRef<HTMLDivElement>(null);

  const { data: planData } = useApi<{ plans: Plan[] }>(
    activeTenantId ? () => plansApi.list(activeTenantId) : null,
    [activeTenantId],
  );

  const activePlans = planData?.plans.filter((p) => p.active) ?? [];

  const { data: rolesData } = useApi(
    activeTenantId ? () => tenantsApi.discordRoles(activeTenantId) : null,
    [activeTenantId],
  );
  const discordRoles = rolesData?.roles ?? [];
  const roleName = (rid: string) => {
    const found = discordRoles.find((r: { id: string; name: string }) => r.id === rid);
    return found ? `@${found.name}` : '@role';
  };

  // which optional sections are visible
  const [showAuthor, setShowAuthor] = useState(false);
  const [showFooter, setShowFooter] = useState(true);
  const [showThumbnail, setShowThumbnail] = useState(false);
  const [showImage, setShowImage] = useState(false);

  // sync form from tenant
  useEffect(() => {
    if (!activeTenant) return;
    const config = activeTenant.embedConfig ?? DEFAULT_EMBED;
    setTitle(config.title);
    setDescription(config.description);
    setColor(config.color);
    setFooterText(config.footerText);
    setFooterIconUrl(config.footerIconUrl ?? '');
    setAuthorName(config.authorName ?? '');
    setAuthorIconUrl(config.authorIconUrl ?? '');
    setAuthorUrl(config.authorUrl ?? '');
    setThumbnailUrl(config.thumbnailUrl ?? '');
    setImageUrl(config.imageUrl ?? '');
    setShowAuthor(!!(config.authorName));
    setShowFooter(!!(config.footerText));
    setShowThumbnail(!!(config.thumbnailUrl));
    setShowImage(!!(config.imageUrl));
  }, [activeTenant]);

  // close popups on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (colorPopupRef.current && !colorPopupRef.current.contains(e.target as Node)) setShowColorPicker(false);
      if (addMenuRef.current && !addMenuRef.current.contains(e.target as Node)) setShowAddMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // helper: upload a file and return the URL
  const handleUpload = async (file: File): Promise<string | null> => {
    try {
      return await uploadFile(file);
    } catch (err) {
      console.error('[embed] upload failed:', err);
      toast(err instanceof Error ? err.message : 'Upload failed', 'error');
      return null;
    }
  };

  const handleSave = async () => {
    if (!activeTenantId) return;
    setSaving(true);
    setSaved(false);
    try {
      await tenantsApi.update(activeTenantId, {
        embedConfig: {
          title, description, color, footerText,
          footerIconUrl: footerIconUrl || undefined,
          authorName: showAuthor ? (authorName || undefined) : undefined,
          authorIconUrl: showAuthor ? (authorIconUrl || undefined) : undefined,
          authorUrl: showAuthor ? (authorUrl || undefined) : undefined,
          thumbnailUrl: showThumbnail ? (thumbnailUrl || undefined) : undefined,
          imageUrl: showImage ? (imageUrl || undefined) : undefined,
        },
      });
      await refreshActiveTenant();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('[embeds] save failed:', err);
      toast('Failed to save embed settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const applyCustomHex = () => {
    const hex = customHex.replace('#', '');
    if (/^[0-9a-fA-F]{6}$/.test(hex)) {
      setColor(parseInt(hex, 16));
    }
  };

  // detect unsaved changes
  const isDirty = useMemo(() => {
    if (!activeTenant) return false;
    const config = activeTenant.embedConfig ?? DEFAULT_EMBED;
    return (
      title !== config.title ||
      description !== config.description ||
      color !== config.color ||
      footerText !== config.footerText ||
      footerIconUrl !== (config.footerIconUrl ?? '') ||
      authorName !== (config.authorName ?? '') ||
      authorIconUrl !== (config.authorIconUrl ?? '') ||
      authorUrl !== (config.authorUrl ?? '') ||
      thumbnailUrl !== (config.thumbnailUrl ?? '') ||
      imageUrl !== (config.imageUrl ?? '')
    );
  }, [activeTenant, title, description, color, footerText, footerIconUrl, authorName, authorIconUrl, authorUrl, thumbnailUrl, imageUrl]);

  if (tenantLoading) return <LoadingSpinner text="Loading embed settings..." />;
  if (tenantError) return <ErrorMessage message={tenantError} onRetry={() => void refreshTenants()} />;
  if (!activeTenant) return <ErrorMessage message="No server selected. You may not be assigned to any server yet." />;

  const hexColor = `#${color.toString(16).padStart(6, '0')}`;

  // fields available to add
  const addableFields = [
    ...(!showAuthor ? [{ key: 'author', label: 'Author', action: () => setShowAuthor(true) }] : []),
    ...(!showFooter ? [{ key: 'footer', label: 'Footer', action: () => setShowFooter(true) }] : []),
    ...(!showThumbnail ? [{ key: 'thumbnail', label: 'Thumbnail Image', action: () => setShowThumbnail(true) }] : []),
    ...(!showImage ? [{ key: 'image', label: 'Embed Image', action: () => setShowImage(true) }] : []),
  ];

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--fd)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>Embeds</h2>
          <p style={{ fontSize: 13, color: 'var(--t3)' }}>Click directly on the embed below to edit it</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-sm btn-primary"
            style={{ padding: '8px 20px', fontSize: 13, position: 'relative' }}
            onClick={() => void handleSave()}
            disabled={saving}
          >
            {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
            {isDirty && !saving && !saved && (
              <span style={{
                position: 'absolute', top: -3, right: -3,
                width: 8, height: 8, borderRadius: '50%',
                background: 'var(--amber)', border: '2px solid var(--bg)',
              }} />
            )}
          </button>
        </div>
      </div>

      {/* WYSIWYG embed builder */}
      <div className="kpi" style={{ padding: 24, maxWidth: 600, overflow: 'visible' }}>
        {/* Bot header decoration */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%', background: '#5865f2',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--t1)' }}>
                {activeTenant.name || 'SubFi Pay'}
              </span>
              <span style={{
                fontSize: 9, fontWeight: 700, background: '#5865f2', color: '#fff',
                padding: '1px 5px', borderRadius: 3, textTransform: 'uppercase', letterSpacing: '0.02em',
              }}>BOT</span>
              <span style={{ fontSize: 11, color: 'var(--t3)' }}>Today</span>
            </div>
          </div>
        </div>

        {/* The embed itself — editable inline */}
        <div style={{
          background: '#2b2d31', borderRadius: 4, overflow: 'visible',
          display: 'flex', marginLeft: 50, position: 'relative',
        }}>
          {/* Color accent bar — clickable to change color */}
          <div
            ref={colorPopupRef}
            style={{ position: 'relative', flexShrink: 0 }}
          >
            <div
              onClick={() => setShowColorPicker(!showColorPicker)}
              title="Change embed color"
              style={{
                width: 4, background: hexColor, borderRadius: '4px 0 0 4px', cursor: 'pointer',
                minHeight: '100%', transition: 'width 0.1s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.width = '6px'; }}
              onMouseLeave={(e) => { e.currentTarget.style.width = '4px'; }}
            />

            {/* color picker popup */}
            {showColorPicker && (
              <div style={{
                position: 'absolute', left: 0, top: '100%', marginTop: 8, zIndex: 30,
                background: '#1e1f22', border: '1px solid #3f4147', borderRadius: 8,
                padding: 12, boxShadow: '0 4px 16px rgba(0,0,0,.5)', width: 200,
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#b5bac1', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Embed Color</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  {COLOR_PRESETS.map((c) => (
                    <div
                      key={c.value}
                      onClick={() => { setColor(c.value); setCustomHex(c.hex); }}
                      style={{
                        width: 24, height: 24, borderRadius: 4, background: c.hex, cursor: 'pointer',
                        border: color === c.value ? '2px solid #fff' : c.hex === '#ffffff' ? '1px solid #4e5058' : '2px solid transparent',
                      }}
                    />
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    value={customHex}
                    onChange={(e) => setCustomHex(e.target.value)}
                    onBlur={applyCustomHex}
                    onKeyDown={(e) => e.key === 'Enter' && applyCustomHex()}
                    placeholder="#hex"
                    style={{
                      flex: 1, background: '#2b2d31', border: '1px solid #3f4147', borderRadius: 4,
                      color: '#f2f3f5', padding: '4px 8px', fontSize: 12, fontFamily: 'monospace', outline: 'none',
                    }}
                  />
                  <div style={{ width: 24, height: 24, borderRadius: 4, background: hexColor, border: '1px solid #3f4147', cursor: 'pointer', position: 'relative', flexShrink: 0 }}>
                    <input
                      ref={colorPickerRef}
                      type="color"
                      value={hexColor}
                      onChange={(e) => {
                        const hex = e.target.value.replace('#', '');
                        setColor(parseInt(hex, 16));
                        setCustomHex(e.target.value);
                      }}
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* embed content */}
          <div style={{ padding: '12px 16px 12px 12px', flex: 1, minWidth: 0, display: 'flex', gap: 16 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* author section */}
              {showAuthor && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  {authorIconUrl ? (
                    <InlineImage
                      url={authorIconUrl}
                      onChangeUrl={setAuthorIconUrl}
                      label="Author Icon"
                      imgStyle={{ width: 20, height: 20, borderRadius: '50%' }}
                      placeholderStyle={{ width: 20, height: 20, borderRadius: '50%' }}
                      onError={(msg) => toast(msg, 'error')}
                    />
                  ) : (
                    <ImagePlaceholder
                      label=""
                      style={{ width: 20, height: 20, borderRadius: '50%', fontSize: 0, padding: 0 }}
                      onPickFile={async (file) => {
                        const url = await handleUpload(file);
                        if (url) setAuthorIconUrl(url);
                      }}
                    />
                  )}
                  <InlineText
                    value={authorName}
                    onChange={setAuthorName}
                    placeholder="Author name"
                    style={{ fontSize: 12, fontWeight: 600, color: authorUrl ? '#00b0f4' : '#f2f3f5', flex: 1 }}
                  />
                  <ActionBtn
                    icon={TrashIcon}
                    title="Remove author"
                    onClick={() => { setShowAuthor(false); setAuthorName(''); setAuthorIconUrl(''); setAuthorUrl(''); }}
                  />
                </div>
              )}

              {/* title */}
              <div style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                <InlineText
                  value={title}
                  onChange={setTitle}
                  placeholder="Embed Title"
                  style={{ fontSize: 15, fontWeight: 700, color: '#f2f3f5', flex: 1 }}
                />
              </div>

              {/* description */}
              <div style={{ marginBottom: 12 }}>
                <InlineText
                  value={description}
                  onChange={setDescription}
                  placeholder="Write your description here..."
                  style={{ fontSize: 14, color: '#b5bac1' }}
                  multiline
                />
              </div>

              {/* fields (plans) — read-only preview */}
              {activePlans.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(activePlans.length, 3)}, 1fr)`, gap: 4, marginBottom: 8 }}>
                  {activePlans.slice(0, 3).map((p) => (
                    <div key={p.id} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#f2f3f5' }}>
                        {p.emoji} {p.label}
                      </div>
                      <div style={{ fontSize: 12, color: '#b5bac1' }}>{roleName(p.roleId)} for {p.durationDays} days</div>
                    </div>
                  ))}
                </div>
              )}

              {/* embed image */}
              {showImage && (
                <div style={{ marginBottom: 8, position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ flex: 1 }}>
                      {imageUrl ? (
                        <InlineImage
                          url={imageUrl}
                          onChangeUrl={setImageUrl}
                          label="Embed Image"
                          imgStyle={{ maxWidth: '100%', borderRadius: 4, maxHeight: 200, objectFit: 'cover', display: 'block' }}
                          onError={(msg) => toast(msg, 'error')}
                        />
                      ) : (
                        <ImagePlaceholder
                          label="Add Image"
                          style={{ height: 80, borderRadius: 4 }}
                          onPickFile={async (file) => {
                            const url = await handleUpload(file);
                            if (url) setImageUrl(url);
                          }}
                        />
                      )}
                    </div>
                    <div style={{ alignSelf: 'flex-start' }}>
                      <ActionBtn
                        icon={TrashIcon}
                        title="Remove image"
                        onClick={() => { setShowImage(false); setImageUrl(''); }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* footer */}
              {showFooter && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, marginTop: 8, borderTop: '1px solid #3f4147', paddingTop: 8 }}>
                  {footerIconUrl ? (
                    <InlineImage
                      url={footerIconUrl}
                      onChangeUrl={setFooterIconUrl}
                      label="Footer Icon"
                      imgStyle={{ width: 16, height: 16, borderRadius: '50%' }}
                      placeholderStyle={{ width: 16, height: 16 }}
                      onError={(msg) => toast(msg, 'error')}
                    />
                  ) : (
                    <ImagePlaceholder
                      label=""
                      style={{ width: 16, height: 16, borderRadius: '50%', fontSize: 0, padding: 0, border: '1.5px dashed #4e5058' }}
                      onPickFile={async (file) => {
                        const url = await handleUpload(file);
                        if (url) setFooterIconUrl(url);
                      }}
                    />
                  )}
                  <InlineText
                    value={footerText}
                    onChange={setFooterText}
                    placeholder="Footer text"
                    style={{ fontSize: 11, color: '#80848e', flex: 1 }}
                  />
                  <ActionBtn
                    icon={TrashIcon}
                    title="Remove footer"
                    onClick={() => { setShowFooter(false); setFooterText(''); setFooterIconUrl(''); }}
                  />
                </div>
              )}
            </div>

            {/* thumbnail — top right */}
            {showThumbnail && (
              <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                {thumbnailUrl ? (
                  <InlineImage
                    url={thumbnailUrl}
                    onChangeUrl={setThumbnailUrl}
                    label="Thumbnail"
                    imgStyle={{ width: 80, height: 80, borderRadius: 4, objectFit: 'cover' }}
                    onError={(msg) => toast(msg, 'error')}
                  />
                ) : (
                  <ImagePlaceholder
                    label="Thumb"
                    style={{ width: 80, height: 80, borderRadius: 4, flexDirection: 'column', gap: 2 }}
                    onPickFile={async (file) => {
                      const url = await handleUpload(file);
                      if (url) setThumbnailUrl(url);
                    }}
                  />
                )}
                <ActionBtn
                  icon={TrashIcon}
                  title="Remove thumbnail"
                  onClick={() => { setShowThumbnail(false); setThumbnailUrl(''); }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Action bar below embed */}
        <div style={{ display: 'flex', gap: 8, marginLeft: 50, marginTop: 8, alignItems: 'center', position: 'relative' }}>
          {addableFields.length > 0 && (
            <div ref={addMenuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowAddMenu(!showAddMenu)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px',
                  background: 'transparent', border: '1.5px dashed var(--b1)', borderRadius: 6,
                  color: 'var(--t3)', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                  transition: 'border-color 0.15s, color 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#5865f2'; e.currentTarget.style.color = 'var(--t1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--b1)'; e.currentTarget.style.color = 'var(--t3)'; }}
              >
                {PlusIcon} Add field
              </button>

              {showAddMenu && (
                <div style={{
                  position: 'absolute', bottom: '100%', left: 0, marginBottom: 4,
                  background: '#1e1f22', border: '1px solid #3f4147', borderRadius: 8,
                  padding: 4, minWidth: 160, boxShadow: '0 4px 12px rgba(0,0,0,.4)', zIndex: 20,
                }}>
                  {addableFields.map((f) => (
                    <div
                      key={f.key}
                      onClick={() => { f.action(); setShowAddMenu(false); }}
                      style={{
                        padding: '7px 12px', fontSize: 13, color: '#e0e0e0', borderRadius: 4,
                        cursor: 'pointer', transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#2b2d31'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      {f.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 16, marginLeft: 50 }}>
          This is how the embed will look when users run <code style={{ background: 'var(--s2)', padding: '1px 4px', borderRadius: 3 }}>/menu</code> in your Discord server.
        </div>
      </div>
    </>
  );
}
